import { Component, computed, inject, Input } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoPipe } from '@jsverse/transloco';

import { COLOR_PALETTES, type ColorPalette, type Theme, type ThemeMode } from '../../models/Theme';
import { ThemeService } from '../../services/theme.service';

@Component({
  selector: 'app-theme-picker',
  templateUrl: './theme-picker.component.html',
  styleUrls: ['./theme-picker.component.scss'],
  standalone: true,
  imports: [
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    TranslocoPipe,
    MatDividerModule,
  ],
})
export class ThemePickerComponent {
  // =============================================================================
  // Public Properties
  // =============================================================================

  @Input() isEnforced = false;

  readonly themeService = inject(ThemeService);

  currentTheme = this.themeService.currentTheme;
  contestPalette = this.themeService.contestPalette;
  availablePalettes = COLOR_PALETTES;

  palettes = computed(() => {
    const contestP = this.contestPalette();
    if (contestP && !this.availablePalettes.some((p) => p.id === contestP.id)) {
      return [contestP, ...this.availablePalettes];
    }
    return this.availablePalettes;
  });

  themeModeIcon = computed(() => {
    switch (this.currentTheme().mode) {
      case 'light':
        return 'light_mode';
      case 'dark':
        return 'dark_mode';
      default:
        return 'settings_brightness';
    }
  });

  // =============================================================================
  // Public Methods
  // =============================================================================

  setThemeMode(mode: ThemeMode): void {
    const newTheme: Theme = {
      ...this.currentTheme(),
      mode,
    };
    this.themeService.setTheme(newTheme);
  }

  setPalette(palette: ColorPalette): void {
    if (this.isEnforced) return;
    const newTheme: Theme = {
      ...this.currentTheme(),
      palette,
    };
    this.themeService.setTheme(newTheme);
  }

  isCurrentPalette(paletteId: string): boolean {
    const currentPaletteId = this.contestPalette()?.id ?? this.currentTheme().palette.id;
    return currentPaletteId === paletteId;
  }
}
